import { Employee } from 'data/users';
import { useAccessToken } from 'providers/AccessTokenProvider';
import React, { useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  FormControl,
  InputGroup,
  Image,
  Alert,
  Badge,
  Card,
  Row,
  Col
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { JobAttachment } from 'services/api/file';
import {
  uploadFileToSharePoint,
  uploadImageToSharePoint,
  getJobFolderId
} from 'services/api/sharepoint';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';

interface FileUploadProps {
  setHasUpdated?: any;
  jobId: string;
  files: JobAttachment[];
  multiple?: boolean;
  jobNumbers?: string[];
  companyNames?: string[];
}

interface SelectedFile {
  file: File;
  preview: string | null;
  id: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  setHasUpdated,
  jobId,
  files,
  multiple = false,
  jobNumbers = [],
  companyNames = []
}) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const { authUser } = useAccessToken();
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;

    const newFiles: SelectedFile[] = [];

    Array.from(fileList).forEach((file, index) => {
      const selectedFile: SelectedFile = {
        file,
        preview: null,
        id: `${Date.now()}-${index}`
      };

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => {
          selectedFile.preview = e.target?.result as string;
          newFiles.push(selectedFile);

          if (newFiles.length === fileList.length) {
            setSelectedFiles(
              multiple ? prev => [...prev, ...newFiles] : newFiles
            );
          }
        };
        reader.readAsDataURL(file);
      } else {
        newFiles.push(selectedFile);

        if (newFiles.length === fileList.length) {
          setSelectedFiles(
            multiple ? prev => [...prev, ...newFiles] : newFiles
          );
        }
      }
    });
  };

  const removeFile = (id: string) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== id));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedFiles.length === 0) {
      setUploadError('Please select at least one file to upload.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      if (!(authUser as any)?.id && !authUser?._id) {
        throw new Error('User not authenticated');
      }

      // SharePoint upload logic
      if (jobNumbers.length === 0 || companyNames.length === 0) {
        throw new Error(
          'Job numbers and company names are required for SharePoint upload'
        );
      }

      // Get job folder ID from SharePoint
      const { results } = await getJobFolderId(jobNumbers, companyNames);

      if (!results || results.length === 0 || !results[0].found) {
        throw new Error(
          'Job folder not found in SharePoint. Please ensure the job exists in SharePoint.'
        );
      }

      const jobFolder = results[0];
      const jobFolderId = jobFolder.folders[0]?.id;

      if (!jobFolderId) {
        throw new Error('Job folder ID not found');
      }

      // Upload files to SharePoint
      const uploadPromises = selectedFiles.map(async selectedFile => {
        const isImage = selectedFile.file.type.startsWith('image/');

        // Upload image to images folder (auto-creates if needed) or file to job folder
        const response = isImage
          ? await uploadImageToSharePoint(jobFolderId, selectedFile.file)
          : await uploadFileToSharePoint(jobFolderId, selectedFile.file);

        return {
          fileName: selectedFile.file.name,
          mimeType: selectedFile.file.type,
          jobId,
          id: response.id,
          fileUrl: response.webUrl,
          creator: authUser as Employee,
          size: response.size.toString()
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      uploadedFiles.forEach(file => {
        files.unshift(file);
      });

      queryClient.invalidateQueries('getJobs');
      setHasUpdated?.(true);
      toast.success(
        `Successfully uploaded ${uploadedFiles.length} file${
          uploadedFiles.length > 1 ? 's' : ''
        } to SharePoint`
      );

      setSelectedFiles([]);
      setUploading(false);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError((error as Error).message);
      setUploading(false);
    }
  };

  return (
    <div>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <InputGroup className="mb-3">
            <FormControl
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              multiple={multiple}
              onChange={handleFileChange}
              hidden
              id="fileInput"
            />
            <InputGroup.Text id="inputGroupFileAddon01">
              <Button
                variant="outline-primary"
                onClick={() => document.getElementById('fileInput')?.click()}
              >
                Choose File{multiple ? 's' : ''}
              </Button>
            </InputGroup.Text>
            <FormControl
              type="text"
              placeholder={`Selected File${multiple ? 's' : ''}`}
              aria-describedby="inputGroupFileAddon01"
              value={
                selectedFiles.length === 0
                  ? ''
                  : selectedFiles.length === 1
                  ? selectedFiles[0].file.name
                  : `${selectedFiles.length} files selected`
              }
              readOnly
            />
          </InputGroup>
        </FormGroup>

        {/* File Preview Section */}
        {selectedFiles.length > 0 && (
          <div className="mb-3">
            {multiple && selectedFiles.length > 1 ? (
              <div
                className="file-preview-container"
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                <style>
                  {`
                    .file-preview-container::-webkit-scrollbar {
                      display: none;
                    }
                  `}
                </style>
                <Row className="g-2">
                  {selectedFiles.map(selectedFile => (
                    <Col key={selectedFile.id} xs={6} md={4}>
                      <Card className="position-relative">
                        {selectedFile.preview ? (
                          <Card.Img
                            variant="top"
                            src={selectedFile.preview}
                            style={{ height: '100px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center bg-light"
                            style={{ height: '100px' }}
                          >
                            <span className="text-muted">File</span>
                          </div>
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-1 btn-icon"
                          onClick={() => removeFile(selectedFile.id)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                        <Card.Body className="p-2">
                          <small className="text-truncate d-block">
                            {selectedFile.file.name}
                          </small>
                          <Badge bg="secondary" className="fs-10">
                            {(selectedFile.file.size / 1024 / 1024).toFixed(2)}{' '}
                            MB
                          </Badge>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              selectedFiles[0]?.preview && (
                <Image
                  src={selectedFiles[0].preview}
                  alt="File preview"
                  fluid
                  style={{ maxWidth: '300px' }}
                />
              )
            )}
          </div>
        )}

        <Button variant="primary" disabled={uploading} type="submit">
          {uploading
            ? 'Uploading...'
            : `Upload ${
                selectedFiles.length > 0 ? selectedFiles.length : ''
              } File${selectedFiles.length > 1 ? 's' : ''} to Server`}
        </Button>
        {uploadError && <Alert variant="danger">{uploadError}</Alert>}
      </Form>
    </div>
  );
};

export default FileUpload;
